import { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/css'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { Button } from 'design-system/ui/atoms/button'
import {
  FileUpIcon,
  FileWarningIcon,
  MoveRightIcon,
  TrashIcon,
} from 'lucide-react'

import { DialogBoxCenter } from '~/design-system/atom/alert-dialog-sheet'
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/design-system/atom/dialog'
import { Input } from '~/design-system/atom/input'
import { useToast } from '~/design-system/atom/use-toast'
import {
  deleteOpenapiSpecsById,
  postOpenapiSpecsCreate,
} from '~/generated/client'
import { LoadingIcon, LoadingIconPlain } from '~/ui/components/base/loadingIcon'
import { scrollBarStyle } from '~/ui/constants/style/common'
import { If } from '~/utils/reactComponent'
import { useLogs, useOpenAPISpecs } from './hooks'
import { getStatusEmoji, getStatusPlain } from './index'

type PipelineState =
  | 'preprocess'
  | 'validate'
  | 'prepare'
  | 'build'
  | 'push'
  | 'test'
  | 'load'
  | 'finish'

export const WarningBox = ({
  title,
  description,
}: {
  title: string
  description: string
}) => {
  return (
    <div className="mb-6 flex w-full max-w-[full] flex-col items-start justify-between gap-[6px] rounded-[8px] border-[.5px] border-red-600 bg-red-50 p-5 pt-4">
      <FileWarningIcon className="text-red-800" height={28} width={28} />
      <div className="mt-[4px] text-[16px] font-[500] text-red-800">
        {title}
      </div>
      <div className="mt-[2px] text-[14px] font-[400] text-red-700">
        {description}
      </div>
    </div>
  )
}

export const FinalizeStepModal = ({
  onFinish,
  setOpen,
  openAPISpec,
  integrationYML,
}: {
  onFinish: (data: Record<string, unknown>) => void
  setOpen: (e: boolean) => void
  openAPISpec: string
  integrationYML: string
}) => {
  const [name, setName] = useState('')
  const { refetch } = useOpenAPISpecs({
    staleTime: 5000,
  })

  const { toast } = useToast()
  const { mutate: addOpenAPISpecMutation, isLoading: addOpenAPISpecLoading } =
    useMutation({
      mutationFn: async ({
        name,
        integrationYML,
        openAPISpec,
      }: {
        name: string
        integrationYML: string
        openAPISpec: string
      }) => {
        const data = await postOpenapiSpecsCreate({
          requestBody: {
            name,
            openApiSpec: openAPISpec,
            authConfig: integrationYML,
          },
        })
        await refetch()
        return data
      },
      onSuccess: (data: Record<string, unknown>) => {
        toast({
          title: 'Tool submitted successfully',
          description: 'Your tool has been submitted successfully',
          variant: 'success',
        })
        onFinish(data)
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description:
            // @ts-expect-error - this is okay for now
            'There was an error adding your tool - ' + (error?.message || ''),
          variant: 'destructive',
        })
      },
    })

  const isOpenAPISpecUploaded = useMemo(() => !!openAPISpec, [openAPISpec])

  return (
    <DialogBoxCenter
      open={true}
      setOpen={setOpen?.bind?.(this)}
      className={clsx(
        ' max-h-[90vh] min-w-[600px] overflow-y-scroll bg-[#fff] px-0 py-0 pb-8',
        scrollBarStyle,
      )}
    >
      <DialogHeader className="-">
        <DialogTitle className=" flex flex-col leading-none">
          <div className=" mb-3  text-[16px] font-[500] text-black-200">
            Finalize your tool
          </div>
          <div className=" text-[13px]  font-[400] text-black-600">
            We just need one more detail to get started.
          </div>
        </DialogTitle>
      </DialogHeader>
      <DialogDescription className=" !mt-[32px] w-full  font-gilroy text-[13px] tracking-[.35px] text-black-300">
        {!isOpenAPISpecUploaded && (
          <WarningBox
            title="No OpenAPI spec uploaded"
            description="This integration will only handle authentication without an OpenAPI spec. Upload a spec to enable additional endpoints."
          />
        )}
        <div className="flex w-full max-w-[full] items-start justify-between  gap-4 ">
          <div>
            <div className=" text-[13.5px] font-[500] text-black-300">
              Tool Name
            </div>
            <div className=" mt-[4px] text-[12px] font-[400] text-black-600">
              Name your tool for identification.
            </div>
          </div>
          <Input
            className="h-[36px] w-[200px] flex-auto "
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="mt-8 flex justify-end">
          <Button
            size="36"
            disabled={addOpenAPISpecLoading}
            onClick={() => {
              if (!/^[a-zA-Z\s-]+$/.test(name)) {
                toast({
                  title: 'Invalid name',
                  description:
                    'Tool name can only contain letters, spaces and hyphens',
                  variant: 'destructive',
                })
                return
              }
              addOpenAPISpecMutation({
                name,
                integrationYML,
                openAPISpec,
              })
            }}
          >
            {addOpenAPISpecLoading ? (
              <>
                <span className="mr-1 mt-[2px]">Adding</span>
                <LoadingIconPlain height={13} width={13} />
              </>
            ) : (
              <>
                <span className="mr-1 mt-[2px]">Next</span>
                <MoveRightIcon height={13} width={13} />
              </>
            )}
          </Button>
        </div>
      </DialogDescription>
    </DialogBoxCenter>
  )
}

export const JobInfoModal = ({
  jobId,
  setOpen,
}: {
  jobId: string
  setOpen: (e: boolean) => void
}) => {
  const { data: logData, isLoading: isLoadingLogs, refetch } = useLogs(jobId)

  const { toast } = useToast()
  const { mutate: deleteOpenAPISpecMutation, isLoading } = useMutation({
    mutationFn: (jobId: string) => {
      return deleteOpenapiSpecsById({
        id: jobId,
      })
    },
    onSuccess: () => {
      toast({
        title: 'Tool deleted successfully',
        description: 'Your tool has been deleted successfully',
        variant: 'success',
      })
      setOpen(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description:
          // @ts-expect-error - this is okay for now
          'There was an error deleting your tool ' + (error?.message || ''),
        variant: 'destructive',
      })
    },
  })
  const { progress, state, status } = isLoadingLogs
    ? {
        progress: { processed: 0, total: 0 },
        state: undefined,
        status: 'loading',
      }
    : logData ||
      ({} as {
        progress: {
          processed: number
          total: number
        }
        state: PipelineState
        status: string
      })

  useEffect(() => {
    refetch()
  }, [])

  return (
    <DialogBoxCenter
      open={true}
      setOpen={setOpen?.bind?.(this)}
      className={clsx(
        ' max-h-[90vh] min-w-[660px] overflow-y-scroll bg-[#fff] px-8 py-6 pb-8',
        scrollBarStyle,
      )}
    >
      <DialogHeader>
        <DialogTitle className="mt-1 flex flex-col   leading-none">
          <div className=" mb-3  text-[16px] font-[500] text-black-200">
            Tool import status
          </div>
          <div className=" text-[13px]  font-[400] text-black-800">
            We're importing your tool. This may take a while.
          </div>
          <hr className="mt-6 border-t-[.5px] border-grey-200" />
        </DialogTitle>
        <DialogDescription className=" !mt-[32px] w-full   font-gilroy text-[13px] tracking-[.35px] text-black-300">
          <If condition={status === 'exited'}>
            <FailedBox />
          </If>
          <If
            condition={
              status === 'running' || status === 'initialized' || isLoadingLogs
            }
          >
            {/* @ts-expect-error - this is okay for now */}
            <InfoBox state={state} />
          </If>
          <If condition={status === 'finished'}>
            <FinishedBox />
          </If>
          <div className="mb-4 mt-4 flex flex-col gap-2 border-t-[.5px] border-grey-200 pt-4">
            <div className="font-black-500 text-[14px] font-[500]">
              Overall Progress
            </div>
            <div className="flex w-full justify-between text-[14px]">
              <span className="font-[500]">Endpoints processed:</span>
              <span>
                {/* @ts-expect-error - this is okay for now */}
                {progress?.processed} / {progress?.total}
              </span>
            </div>
            <div className="flex w-full justify-between text-[14px]">
              <span className="font-[500]">Status:</span>
              <span>
                {/* @ts-expect-error - this is okay for now */}
                {getStatusEmoji(status)} - {getStatusPlain(status)}
              </span>
            </div>
          </div>
          <div className="text-18px mb-4 mt-4 border-t-[.5px] border-grey-200 pt-4 font-[500] text-black-200">
            Logs for your import jobs.
          </div>
          <div className="h-[200px] max-w-[640px] overflow-y-auto  rounded-[12px] border-[1px] border-grey-200 p-5">
            {isLoadingLogs && (
              <div className="flex items-center gap-2">
                <LoadingIconPlain height={20} width={20} />
                Loading logs
              </div>
            )}
            {/* @ts-expect-error - this is okay for now */}
            {logData?.logs?.map(
              (
                log: {
                  type: string
                  message: string
                },
                i: number,
              ) => {
                if (!log?.type) return null

                return (
                  <div
                    className={clsx('mb-5 flex w-[full] flex-wrap gap-3')}
                    key={i}
                  >
                    <div
                      style={{
                        background:
                          log.type === 'warning' || log.type === 'error'
                            ? 'brown'
                            : 'green',
                        height: '20px',
                      }}
                      className="w-[4px] max-w-[6px] rounded-[2px] font-[500]"
                    ></div>
                    {!!log.message && (
                      <div className="flex-1 text-wrap">{log?.message}</div>
                    )}
                  </div>
                )
              },
            )}
            {status === 'running' && (
              <div className="mb-5 flex items-center gap-2">
                <LoadingIconPlain height={20} width={20} />
                Waiting for more logs
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              className="flex w-[100px] gap-1 !bg-[#c61f50]"
              disabled={isLoading}
              onClick={() => {
                deleteOpenAPISpecMutation(jobId)
              }}
            >
              {isLoading ? (
                <>
                  <LoadingIconPlain height={13} width={13} />
                  <span className="mt-[2px]">Deleting</span>
                </>
              ) : (
                <>
                  <span className="mt-[2px]">Delete</span>
                  <TrashIcon height={13} width={13} />
                </>
              )}
            </Button>
          </div>
        </DialogDescription>
      </DialogHeader>
    </DialogBoxCenter>
  )
}

export const FinishedBox = () => {
  return (
    <div
      className={clsx(
        'mb-4 mt-[-20px] flex w-full justify-between gap-2',
        css`
          background: #1e1e1e;
          padding: 15px;
          border: 1px solid #000000;
          color: #fff;
          border-radius: 13px;
        `,
      )}
    >
      <div>
        <div className="flex items-center gap-2 ">
          <FileUpIcon height={16} width={16} />
          <span className="font-black-500 text-[14px] font-[500] ">
            Pipeline finished processing
          </span>
        </div>
        <div className="mt-[10px]">
          Your tool will show up on the dashboard after post-processing is
          finished.
        </div>
      </div>
    </div>
  )
}

export const FailedBox = () => {
  return (
    <div
      className={clsx(
        'mb-4 mt-[-20px] flex w-full justify-between gap-2',
        css`
          background: #1e1e1e;
          padding: 15px;
          border: 1px solid #000000;
          color: #fff;
          border-radius: 13px;
        `,
      )}
    >
      <div>
        <div className="flex items-center gap-2 ">
          <FileUpIcon height={16} width={16} />
          <span className="font-black-500 text-[14px] font-[500] ">
            Import failed
          </span>
        </div>
        <div className="mt-[10px]">
          There is some issue with OpenAPI Spec. We'll get back to you in 12-24
          hours.
        </div>
      </div>
    </div>
  )
}

function getStatusText(state: PipelineState): string {
  switch (state) {
    case 'validate':
      return 'Validating the specification and integration'

    case 'prepare':
      return 'Preparing pipeline'

    case 'preprocess':
      return 'Processing specification YAML (This might take some time)'

    case 'build':
      return 'Building the app'

    case 'push':
      return 'Publishing the app'

    case 'test':
      return 'Testing the app build'

    case 'finish':
      return 'Finished building the app'

    default:
      return 'Waiting for pipeline to start'
  }
}

export const InfoBox = ({ state }: { state: PipelineState }) => {
  return (
    <div
      className={clsx(
        'mb-4 mt-[-20px] flex w-full justify-between gap-2',
        css`
          background: #1e1e1e;
          padding: 15px;
          border: 1px solid #000000;
          color: #fff;
          border-radius: 13px;
        `,
      )}
    >
      <div>
        <div className="flex items-center gap-2">
          <LoadingIcon height={16} width={16} />
          <span className="font-black-500 text-[14px] font-[500] ">
            {!!state ? 'Processing' : 'Loading...'}
          </span>
        </div>
        <div className="mt-[10px]">{getStatusText(state)}</div>
      </div>
    </div>
  )
}
